import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/returns.scss";

const returnsSections = [
  ["overview", "At a glance"],
  ["eligibility", "Return eligibility"],
  ["start", "Starting a return"],
  ["refunds", "Refunds"],
  ["exchanges", "Exchanges"],
  ["faults", "Damaged or incorrect items"],
  ["exceptions", "Exceptions"],
  ["contact-returns", "Need help?"],
];

function Returns() {
  const { brandName, contact, shipping } = siteConfig;

  return (
    <main className="policy-page returns" aria-labelledby="returns-title">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Client care / Returns</p>
        <h1 id="returns-title">Returns &amp; refunds</h1>
        <div className="policy-page__summary">
          <p>
            We want every {brandName} piece to sit and feel right. If it does
            not, eligible items can be returned within
            {` ${shipping.returnsWindowDays} days `}
            of delivery.
          </p>
          <time dateTime="2026-08-14">Policy updated 14 August 2026</time>
        </div>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__contents" aria-label="Returns policy contents">
          <p>On this page</p>
          <ol>
            {returnsSections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-page__content">
          <section id="overview">
            <h2>At a glance</h2>
            <div className="policy-page__metrics" aria-label="Key return information">
              <div>
                <strong>{shipping.returnsWindowDays}</strong>
                <span>Days to request a return</span>
              </div>
              <div>
                <strong>5–10</strong>
                <span>Business days for bank processing</span>
              </div>
              <div>
                <strong>Original</strong>
                <span>Condition and packaging required</span>
              </div>
            </div>
            <p>
              Contact us within {shipping.returnsWindowDays} calendar days after
              delivery. Once your request is accepted, the parcel must be
              handed to the nominated carrier within the timeframe given in
              your instructions.
            </p>
          </section>

          <section id="eligibility">
            <h2>Return eligibility</h2>
            <p>To be accepted, a returned item must be:</p>
            <ul>
              <li>unworn, unwashed and free from fragrance, damage or marks;</li>
              <li>complete with tags, protective materials and accessories;</li>
              <li>securely packed in its original product packaging; and</li>
              <li>accompanied by the order or return reference.</li>
            </ul>
            <p>
              Please handle pieces only as needed to inspect them. We may reduce
              a refund where handling has diminished the item’s value, as
              permitted by law.
            </p>
          </section>

          <section id="start">
            <h2>Starting a return</h2>
            <ol className="policy-page__steps">
              <li>
                <span>01</span>
                <div>
                  <h3>Tell us</h3>
                  <p>
                    Use the contact form with your order number, the item and
                    the reason for return.
                  </p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <h3>Wait for authorisation</h3>
                  <p>
                    We will confirm eligibility and send the correct return
                    address and packing instructions.
                  </p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <h3>Pack and send</h3>
                  <p>
                    Repack the item carefully and retain the carrier receipt
                    until your refund is complete.
                  </p>
                </div>
              </li>
            </ol>
            <div className="policy-page__notice">
              Returns sent without authorisation, cash-on-delivery parcels and
              items sent to a different address may be delayed or refused.
            </div>
          </section>

          <section id="refunds">
            <h2>Refunds</h2>
            <p>
              We inspect returns after arrival and email you with the outcome.
              Approved refunds are issued to the original payment method.
              Please allow 5–10 business days after processing for your bank or
              card provider to display the credit.
            </p>
            <p>
              Original express delivery charges are not refundable unless the
              order was faulty, incorrect or the law requires otherwise. Return
              delivery costs may be deducted from the refund when a prepaid
              service is supplied; the amount will be shown before you proceed.
            </p>
          </section>

          <section id="exchanges">
            <h2>Exchanges</h2>
            <p>
              We cannot reserve stock while a return is travelling. For the
              quickest replacement, return the original item and place a new
              order. Our team can help confirm availability before you do so.
            </p>
          </section>

          <section id="faults">
            <h2>Damaged or incorrect items</h2>
            <p>
              Check your delivery promptly. If an item arrives damaged,
              defective or different from what you ordered, contact us within
              48 hours with the order number and clear photographs of the item,
              packaging and shipping label. Keep all packaging while we review
              the issue.
            </p>
            <p>
              Where the issue is confirmed, we will arrange an appropriate
              repair, replacement or refund and cover reasonable return costs.
              Your statutory rights are unaffected.
            </p>
          </section>

          <section id="exceptions">
            <h2>Exceptions</h2>
            <p>Unless faulty or required by law, returns are not available for:</p>
            <ul>
              <li>personalised, made-to-order or altered pieces;</li>
              <li>gift cards and final-sale items clearly marked as such;</li>
              <li>items damaged through misuse or incorrect care; or</li>
              <li>sealed hygiene-sensitive goods once unsealed.</li>
            </ul>
          </section>

          <section id="contact-returns">
            <h2>Need help?</h2>
            <p>
              Our client care team can check eligibility, packaging requirements
              and return progress before you send anything.
            </p>
            <div className="policy-page__callout">
              <p>Start or discuss a return</p>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
              <span>{contact.hours}</span>
              <Link to="/contact">Open the contact form ↗</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Returns;
